import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { GroupsOutlined as GroupsOutlinedIcon } from 'components/icons';
import type { CellContext } from '@tanstack/react-table';
import { useMemo } from 'react';

import StatusChip from 'components/@extended/StatusChip';
import DmsTeamStatusIndicator from 'components/dms/DmsTeamStatusIndicator';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Tooltip } from 'components/ui/tooltip';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Link as RouterLink } from 'react-router-dom';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { useLazyGetTeamsWithFiltersQuery } from 'store/api/teams';
import { FeatureFlag } from 'types/features';
import { Team } from 'types/teams';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

const LINK_CLASS = 'entity-link text-fg hover:text-link font-semibold no-underline hover:underline';

const getTeamHref = (team: Team) => `/team/${team.id}`;

const TeamsTable = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const [fetchTeams, { data, isFetching }] = useLazyGetTeamsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchTeams);

    const { classifiers } = useClassifiers({ codes: ['TEAM_TYPE'] });
    const isDmsTeamAdIntegrationEnabled = useIsFeatureEnabled(FeatureFlag.DMS_TEAM_AD_INTEGRATION_ENABLED);

    const columns = useMemo<DataTableColumnDef<Team>[]>(
        () => [
            {
                header: t('name'),
                id: 'name',
                // A column is sortable only with an accessor; the value is unused (the Cell renders
                // the linked name). Same rule for the parent-team and AD-status columns below.
                accessorKey: 'name',
                meta: {
                    widthWeight: 2.2,
                    minPx: 200,
                    minWidth: 250,
                },
                cell: ({ row }) => {
                    const { id, name, isDraft } = row.original;
                    return (
                        <DraftNameCell draft={isDraft ? { type: 'team', id } : null}>
                            <div className="flex flex-col">
                                <RouterLink className={LINK_CLASS} to={`/team/${id}`}>
                                    {name || (isDraft ? t('new-entity.team') : '-')}
                                </RouterLink>
                            </div>
                        </DraftNameCell>
                    );
                },
            },
            {
                header: t('initials'),
                id: 'abbreviation',
                accessorKey: 'abbreviation',
                meta: {
                    widthWeight: 1,
                    minPx: 90,
                    width: 80,
                },
                cell: ({ getValue }) => getValue<Team['abbreviation']>() ?? '',
            },
            {
                header: t('teams.parent-team'),
                id: 'parentTeam.name',
                accessorFn: (row) => row.parentTeam?.name,
                meta: {
                    widthWeight: 1.3,
                    minPx: 130,
                },
                cell: ({ row }) => {
                    const { parentTeam } = row.original;
                    return parentTeam ? (
                        <RecordLink
                            className={LINK_CLASS}
                            to={`/team/${parentTeam.id}`}
                            label={parentTeam.name ?? '-'}
                        />
                    ) : (
                        '-'
                    );
                },
            },
            {
                header: t('type'),
                id: 'teamTypeCode',
                accessorKey: 'teamTypeCode',
                meta: {
                    widthWeight: 1.25,
                    minPx: 120,
                    width: 100,
                },
                cell: ({ getValue }) =>
                    classifiers['TEAM_TYPE']
                        ? getClassifierText(classifiers['TEAM_TYPE'], getValue<Team['teamTypeCode']>() ?? undefined)
                        : '',
            },
            {
                header: t('email'),
                id: 'emailAddress',
                accessorKey: 'emailAddress',
                meta: {
                    widthWeight: 1.55,
                    minPx: 150,
                },
                cell: ({ getValue }) => getValue<Team['emailAddress']>() ?? '',
            },
            ...(isDmsTeamAdIntegrationEnabled
                ? [
                      {
                          header: t('teams.ad-status'),
                          id: 'dmsTeamName',
                          accessorKey: 'dmsTeamName',
                          meta: {
                              widthWeight: 1.3,
                              minPx: 130,
                          },
                          cell: ({ row }: CellContext<Team, unknown>) => {
                              const { dmsTeamId, dmsTeamName, dmsTeamValid } = row.original;
                              if (!dmsTeamId) {
                                  return '-';
                              }
                              const displayName = dmsTeamName || dmsTeamId;
                              const truncatedName = (
                                  <Tooltip content={displayName}>
                                      <span
                                          className="inline-block max-w-[150px] truncate"
                                          aria-label={displayName}
                                          role="text"
                                      >
                                          {displayName}
                                      </span>
                                  </Tooltip>
                              );
                              return (
                                  <div className="flex items-center gap-1">
                                      {truncatedName}
                                      {dmsTeamValid === false && <DmsTeamStatusIndicator isValid={false} />}
                                  </div>
                              );
                          },
                      } as DataTableColumnDef<Team>,
                  ]
                : []),
            {
                header: t('status.status'),
                id: 'isActive',
                accessorKey: 'isActive',
                meta: {
                    widthWeight: 0.95,
                    minPx: 100,
                    width: 100,
                },
                cell: ({ getValue }) => {
                    switch (getValue<Team['isActive']>()) {
                        case false:
                            return <StatusChip label={t('not-active')} type="error" />;
                        case true:
                            return <StatusChip label={t('active')} type="success" />;
                        default:
                            return <StatusChip label={t('unknown')} type="inactive" />;
                    }
                },
            },
        ],
        [classifiers, isDmsTeamAdIntegrationEnabled]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'isActive',
                    chipLabel: 'filters.status',
                    filters: [
                        {
                            label: t('active'),
                            argument: 'true',
                        },
                        {
                            label: t('not-active'),
                            argument: 'false',
                        },
                    ],
                },
            ],
            filters: [],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'abbreviation' },
                { selector: 'emailAddress' },
                { selector: 'parentTeam.name' },
            ],
        }),
        []
    );

    return (
        <ListTableWithFilters<Team>
            getRowHref={getTeamHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="teams"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="teams.teams"
            singularTextId="teams.team"
            noEntriesId="teams.no-teams-found"
            noEntriesBodyId="teams.empty.body"
            noEntriesIcon={GroupsOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.TEAMS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/team/new')}
                    labelId="teams.add-new-team"
                    newLabelId="new-entity.team"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default TeamsTable;
